import { Camera, Color, Scene } from 'three';

import type { DebugManager } from '../../engine/managers/debug-manager';
import { discoverLights } from './discover-lights';
import { LightHelper } from './light-helper.class';
import {
	getLightType,
	LIGHT_HELPER_DEFAULTS,
	LightHelperManagerConfig,
	RenderQuality,
	VisualizableLight
} from './light-helper.types';
import { LightInteraction } from './light-interaction';
import { LightColorChangeCallback, LightInteractionConfig } from './light-interaction.types';

/**
 * Extended configuration for LightHelperManager with interaction support
 */
export interface LightHelperManagerConfigExtended extends LightHelperManagerConfig {
	/** Camera for raycasting (required for interaction) */
	camera?: Camera;
	/** DOM element for event listeners (required for interaction) */
	domElement?: HTMLElement;
	/** Interaction configuration */
	interactionConfig?: LightInteractionConfig;
	/** Debug manager for tooltip registration */
	debugManager?: DebugManager;
}

/**
 * Manages visualization helpers for all lights in a scene
 *
 * Features:
 * - Auto-discovers DirectionalLight and SpotLight instances
 * - Creates and manages LightHelper for each light
 * - Global visibility control
 * - Automatic cleanup of removed lights
 * - Interactive color picker on spheres (optional)
 *
 * @example
 * ```typescript
 * const manager = new LightHelperManager({
 *   scene,
 *   visible: true,
 *   camera,
 *   domElement: canvas
 * });
 *
 * // Enable interaction
 * manager.enableInteraction();
 *
 * // In render loop
 * manager.update();
 *
 * // Toggle visibility
 * manager.setVisible(false);
 *
 * // Clean up
 * manager.dispose();
 * ```
 */
export class LightHelperManager {
	/** Scene to manage */
	private scene: Scene;

	/** Map of light to helper */
	private helpers: Map<VisualizableLight, LightHelper> = new Map();

	/** Configuration */
	#coneRadius: number;
	#coneHeight: number;
	#visible: boolean;
	#quality: RenderQuality;

	/** Interaction system */
	#camera: Camera | null = null;
	#domElement: HTMLElement | null = null;
	#interaction: LightInteraction | null = null;
	#interactionConfig: LightInteractionConfig;
	#interactionEnabled: boolean = false;

	/** Debug manager for tooltip registration */
	#debugManager: DebugManager | null = null;

	constructor (config: LightHelperManagerConfigExtended) {
		this.scene = config.scene;
		this.#coneRadius = config.coneRadius ?? LIGHT_HELPER_DEFAULTS.CONE_RADIUS;
		this.#coneHeight = config.coneHeight ?? LIGHT_HELPER_DEFAULTS.CONE_HEIGHT;
		this.#visible = config.visible ?? false;
		this.#quality = config.quality ?? LIGHT_HELPER_DEFAULTS.QUALITY;

		// Store interaction dependencies
		this.#camera = config.camera ?? null;
		this.#domElement = config.domElement ?? null;
		this.#interactionConfig = config.interactionConfig ?? {};

		// Store debug manager for tooltip registration
		this.#debugManager = config.debugManager ?? null;
	}

	/** Get visibility state */
	get visible (): boolean {
		return this.#visible;
	}

	/** Get number of managed helpers */
	get count (): number {
		return this.helpers.size;
	}

	/** Check if interaction is enabled */
	get interactionEnabled (): boolean {
		return this.#interactionEnabled;
	}

	/** Check if interaction is currently active (user is dragging) */
	get isInteracting (): boolean {
		return this.#interaction?.isActive ?? false;
	}

	/**
	 * Check if click events should be suppressed
	 * Returns true if the color wheel interaction just ended
	 */
	shouldSuppressClick (): boolean {
		return this.#interaction?.shouldSuppressClick() ?? false;
	}

	/**
	 * Set the camera (needed for interaction)
	 */
	setCamera (camera: Camera): void {
		this.#camera = camera;

		// If interaction is enabled, reinitialize it
		if (this.#interactionEnabled && this.#interaction) {
			this.disableInteraction();
			this.enableInteraction();
		}
	}

	/**
	 * Set the DOM element (needed for interaction)
	 */
	setDomElement (element: HTMLElement): void {
		this.#domElement = element;

		// If interaction is enabled, reinitialize it
		if (this.#interactionEnabled && this.#interaction) {
			this.disableInteraction();
			this.enableInteraction();
		}
	}

	/**
	 * Enable interactive color picking on light spheres
	 *
	 * @returns true if interaction was enabled successfully
	 */
	enableInteraction (): boolean {
		if (this.#interactionEnabled) return true;

		if (!this.#camera || !this.#domElement) {
			console.warn('LightHelperManager: Cannot enable interaction without camera and domElement');
			return false;
		}

		this.#interaction = new LightInteraction(
			this.scene,
			this.#camera,
			this.#domElement,
			this.#interactionConfig
		);

		// Register all existing helpers
		for (const helper of this.helpers.values()) {
			this.#interaction.registerHelper(helper);
		}

		this.#interactionEnabled = true;
		return true;
	}

	/**
	 * Disable interactive color picking
	 */
	disableInteraction (): void {
		if (!this.#interactionEnabled || !this.#interaction) return;

		this.#interaction.dispose();
		this.#interaction = null;
		this.#interactionEnabled = false;
	}

	/**
	 * Set callback for when a light's color is changed via interaction
	 */
	setOnColorChange (callback: LightColorChangeCallback | null): void {
		this.#interaction?.setOnColorChange(callback);
	}

	/**
	 * Set callback for when interaction starts (e.g., to disable camera controls)
	 */
	setOnInteractionStart (callback: (() => void) | null): void {
		this.#interaction?.setOnInteractionStart(callback);
	}

	/**
	 * Set callback for when interaction ends (e.g., to re-enable camera controls)
	 */
	setOnInteractionEnd (callback: (() => void) | null): void {
		this.#interaction?.setOnInteractionEnd(callback);
	}

	/**
	 * Set the debug manager for tooltip registration
	 */
	setDebugManager (debugManager: DebugManager | null): void {
		// Unregister existing tooltips if changing debug managers
		if (this.#debugManager && debugManager !== this.#debugManager) {
			for (const [light, helper] of this.helpers) {
				this.unregisterLightTooltip(light, helper);
			}
		}

		this.#debugManager = debugManager;

		// Register tooltips for existing helpers with new debug manager
		if (debugManager) {
			for (const [light, helper] of this.helpers) {
				this.registerLightTooltip(light, helper);
			}
		}
	}

	/**
	 * Register a light's sphere with the debug manager for tooltip display
	 */
	private registerLightTooltip (light: VisualizableLight, helper: LightHelper): void {
		if (!this.#debugManager) return;

		const lightType = getLightType(light);
		const lightName = light.name || `${lightType}`;
		const tooltipId = `light-${light.uuid}`;

		// Register the helper's sphere directly for tooltip hover detection
		this.#debugManager.registerExternalSphere(tooltipId, helper.sphere, lightName);
	}

	/**
	 * Unregister a light's tooltip from the debug manager
	 */
	private unregisterLightTooltip (light: VisualizableLight, _helper: LightHelper): void {
		if (!this.#debugManager) return;

		const tooltipId = `light-${light.uuid}`;
		this.#debugManager.unregisterExternalSphere(tooltipId);
	}

	/**
	 * Set visibility for all helpers
	 */
	setVisible (visible: boolean): void {
		this.#visible = visible;
		for (const helper of this.helpers.values()) {
			helper.visible = visible;
		}
	}

	/**
	 * Set cone dimensions for all helpers
	 */
	setConeDimensions (radius: number, height: number): void {
		this.#coneRadius = radius;
		this.#coneHeight = height;
		for (const helper of this.helpers.values()) {
			helper.setConeDimensions(radius, height);
		}
	}

	/**
	 * Get render quality (0=wireframe, 1-10=quality level)
	 */
	get quality (): RenderQuality {
		return this.#quality;
	}

	/**
	 * Set render quality for all helpers (0=wireframe, 1-10=quality level)
	 */
	set quality (value: RenderQuality) {
		if (this.#quality === value) return;
		this.#quality = value;
		for (const helper of this.helpers.values()) {
			helper.quality = value;
		}
	}

	/**
	 * Set render quality for all helpers (0=wireframe, 1-10=quality level)
	 */
	setQuality (value: RenderQuality): void {
		this.quality = value;
	}

	/**
	 * Get helper for a specific light
	 */
	getHelper (light: VisualizableLight): LightHelper | undefined {
		return this.helpers.get(light);
	}

	/**
	 * Get all helpers
	 */
	getAllHelpers (): LightHelper[] {
		return Array.from(this.helpers.values());
	}

	/**
	 * Update all helpers
	 *
	 * Call this in your render loop. It will:
	 * - Discover new lights and create helpers for them
	 * - Update existing helpers to match light state
	 * - Clean up helpers for removed lights
	 */
	update (): void {
		// Discover all lights in scene
		const lights = discoverLights(this.scene);

		// Create helpers for new lights
		for (const light of lights) {
			if (!this.helpers.has(light)) {
				const helper = new LightHelper(light, {
					scene: this.scene,
					coneRadius: this.#coneRadius,
					coneHeight: this.#coneHeight,
					visible: this.#visible,
					quality: this.#quality
				});
				this.helpers.set(light, helper);

				// Register with interaction system
				if (this.#interactionEnabled && this.#interaction) {
					this.#interaction.registerHelper(helper);
				}

				// Register tooltip with debug manager
				this.registerLightTooltip(light, helper);
			}
		}

		// Update existing helpers
		for (const helper of this.helpers.values()) {
			helper.update();
		}

		// Clean up helpers for removed lights
		const lightSet = new Set(lights);
		for (const [light, helper] of this.helpers.entries()) {
			if (!lightSet.has(light)) {
				// Unregister from interaction
				if (this.#interactionEnabled && this.#interaction) {
					this.#interaction.unregisterHelper(helper);
				}

				// Unregister tooltip from debug manager
				this.unregisterLightTooltip(light, helper);

				helper.dispose();
				this.helpers.delete(light);
			}
		}
	}

	/**
	 * Force refresh - dispose and recreate all helpers
	 */
	refresh (): void {
		// Unregister all helpers from interaction and tooltips
		for (const [light, helper] of this.helpers) {
			if (this.#interactionEnabled && this.#interaction) {
				this.#interaction.unregisterHelper(helper);
			}
			this.unregisterLightTooltip(light, helper);
		}

		for (const helper of this.helpers.values()) {
			helper.dispose();
		}
		this.helpers.clear();
		this.update();
	}

	/**
	 * Dispose of all helpers and interaction
	 */
	dispose (): void {
		this.disableInteraction();

		// Unregister tooltips and dispose helpers
		for (const [light, helper] of this.helpers) {
			this.unregisterLightTooltip(light, helper);
			helper.dispose();
		}
		this.helpers.clear();
	}
}
